<template>
  <div class="the-hub container-fluid main-content">
    <div class="row">
      <TheHubSidebar :active-slug="activeCategory || (article && article.categories && article.categories[0] ? article.categories[0].slug : '')" />

      <TheHubSkeleton v-if="$fetchState.pending" type="article" class="col-12 col-md-9" />
      <article v-else-if="article" class="col-12 col-md-9 post-view">
        <h1 class="page-title post-title">{{ article.post_title }}</h1>
        <div class="post-meta">
          <span>{{ formatDate(article.post_date) }}</span>
          <span><font-awesome-icon :icon="['fas', 'user']" /> magazine.co.uk</span>
          <span v-if="(article.categories || []).length">
            <font-awesome-icon :icon="['fas', 'folder']" />
            <span v-for="(category, index) in article.categories" :key="category.category_id" class="post-meta__category"
              ><template v-if="index > 0">, </template><a :href="categoryUrl(category.slug)">{{ categoryName(category) }}</a></span
            >
          </span>
        </div>

        <nav v-if="previous || next" class="post-skips" aria-label="Article navigation">
          <nuxt-link v-if="previous" :to="siblingLink(previous)" class="post-skip post-skip--previous" rel="prev">&larr; Previous Post</nuxt-link>
          <nuxt-link v-if="next" :to="siblingLink(next)" class="post-skip post-skip--next" rel="next">Next Post &rarr;</nuxt-link>
        </nav>

        <!-- Sanitized WordPress HTML served by our own articles API -->
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="post-entry article-content" v-html="content"></div>

        <nav v-if="previous || next" class="post-skips post-skips--bottom" aria-label="Article navigation">
          <nuxt-link v-if="previous" :to="siblingLink(previous)" class="post-skip post-skip--previous" rel="prev">&larr; Previous Post</nuxt-link>
          <nuxt-link v-if="next" :to="siblingLink(next)" class="post-skip post-skip--next" rel="next">Next Post &rarr;</nuxt-link>
        </nav>
      </article>
      <div v-else class="col-12 col-md-9">
        <TheHubErrorMessage title="Sorry, we couldn’t load this article" @retry="$fetch" />
      </div>
    </div>
  </div>
</template>

<script>
import { fetchArticleBySlug, formatArticleDate, articlePath, sanitizeArticleHtml, articleDescription } from '~/lib/util/articles'
import { getCategoryBySlug, categoryPath } from '~/lib/data/the-hub-categories'
import TheHubSkeleton from '~/components/TheHub/Skeleton.vue'
import TheHubErrorMessage from '~/components/TheHub/ErrorMessage.vue'

export default {
  name: 'TheHubArticle',
  layout: 'TheHub',
  // Reuse the page between articles; a shimmer skeleton covers the load instead of a blank flash.
  key: () => 'the-hub-article',
  scrollToTop: true,
  components: {
    TheHubSidebar: () => import('~/components/TheHub/Sidebar.vue'),
    TheHubSkeleton,
    TheHubErrorMessage
  },

  async fetch() {
    this.$store.commit('UI/statusCode', 200, { root: true })
    const result = await fetchArticleBySlug({ context: this, slug: this.$route.params.slug, categorySlug: String(this.$route.query.category || '') })

    if (result.statusCode === 404) {
      if (process.server) {
        this.$nuxt.context.res.statusCode = 404
      }
      this.$store.commit('UI/statusCode', 404, { root: true })
      return
    }
    this.article = result.article || null
    this.previous = result.previous || null
    this.next = result.next || null
    this.activeCategory = result.activeCategory || ''
  },

  data() {
    return { article: null, previous: null, next: null, activeCategory: '' }
  },

  computed: {
    content() {
      return sanitizeArticleHtml(this.article?.post_content)
    }
  },
  watch: {
    '$route.params.slug': '$fetch',
    '$route.query.category': '$fetch'
  },

  methods: {
    articlePath,
    // Keep browsing the same category; without ?category= prev/next follow the main Hub list.
    siblingLink(item) {
      const query = this.activeCategory ? { category: this.activeCategory } : {}
      return { path: articlePath(item), query }
    },
    formatDate: formatArticleDate,
    categoryUrl(slug) {
      return `${this.$config.baseURL}${categoryPath({ slug })}`
    },
    categoryName(category) {
      return getCategoryBySlug(category.slug)?.name || String(category.name).replace(/&amp;/g, '&')
    }
  },

  head() {
    const robots = { hid: 'robots', name: 'robots', content: this.$config.robots || 'INDEX,FOLLOW' }
    if (!this.article) return { title: 'The Hub | magazine.co.uk', meta: [robots] }

    const url = `${this.$config.baseURL}${articlePath(this.article)}`
    const description = articleDescription(this.article.post_content)
    return {
      title: `${this.article.post_title} | The Hub | magazine.co.uk`,
      meta: [
        { hid: 'description', name: 'description', content: description },
        { hid: 'keywords', name: 'keywords', content: 'Magazine.co.uk' },
        robots,
        { hid: 'og:locale', property: 'og:locale', content: 'en_GB' },
        { hid: 'og:type', property: 'og:type', content: 'article' },
        { hid: 'og:title', property: 'og:title', content: `${this.article.post_title} | by magazine.co.uk` },
        { hid: 'og:description', property: 'og:description', content: description },
        { hid: 'og:url', property: 'og:url', content: url },
        { hid: 'og:site_name', property: 'og:site_name', content: 'magazine.co.uk' }
      ],
      link: [{ hid: 'canonical', rel: 'canonical', href: url }]
    }
  }
}
</script>

<style lang="scss" scoped>
.the-hub {
  max-width: 1200px;
  margin: 0 auto;
}
.post-view .post-entry {
  max-width: 100%;
  overflow: visible;
  height: auto;
}
.post-view .page-title {
  color: #e1000b;
  font-size: 40px;
  font-weight: 400;
  line-height: 1.2;
  margin: 10px 0 20px;
}
.post-meta {
  font-style: normal;
  font-size: 15px;
  line-height: 22px;
  margin: 0 0 20px;
  padding-left: 10px;
  span {
    display: inline-block;
    margin-right: 35px;
  }
  .post-meta__category {
    display: inline;
    margin-right: 0;
  }
  a {
    color: #363636;
  }
}
// Overrides the absolutely-positioned `.post-view .post-skips` rules in the-hub-style.css.
.post-view .post-skips {
  position: static;
  height: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 25px;
  &--bottom {
    margin: 30px 0 0;
    padding-top: 20px;
    border-top: 1px solid #e9e9e9;
  }
}
.post-skip {
  font-size: 16px;
  color: #363636;
  text-decoration: none;
  &:hover {
    color: #e1000b;
  }
  &--next {
    margin-left: auto;
  }
}
@media (max-width: #{$screen-sm-min - 1}) {
  .post-view .page-title {
    font-size: 26px;
    margin: 15px 0;
  }
  .post-meta {
    padding-left: 0;
    span {
      margin-right: 20px;
    }
  }
  .post-skip {
    font-size: 15px;
  }
}

// Styles for the WordPress HTML injected through v-html.
.article-content {
  ::v-deep {
    h1,
    h2,
    h3,
    h4 {
      color: #363636;
      font-weight: 700;
      line-height: 1.3;
      margin: 30px 0 15px;
    }
    h1 {
      font-size: 32px;
    }
    h2 {
      font-size: 26px;
    }
    h3 {
      font-size: 22px;
    }
    a {
      color: #3a6eb5;
    }
    ul,
    ol {
      margin: 0 0 1em 1.5em;
      font-size: 16px;
      line-height: 28px;
    }
    img {
      max-width: 100%;
      height: auto;
    }
    figure {
      margin: 20px 0;
    }
    figcaption {
      text-align: center;
      font-size: 13px;
      color: #636467;
    }
    hr,
    .wp-block-separator {
      border: 0;
      border-top: 1px solid #e9e9e9;
      margin: 30px 0;
    }
    blockquote {
      margin: 20px 0;
      padding: 10px 20px;
      border-left: 4px solid #e1000b;
      background: #f2f2f2;
    }
    table {
      width: 100%;
      margin-bottom: 1em;
      border-collapse: collapse;
    }
    td,
    th {
      padding: 6px 10px;
      border: 1px solid #e9e9e9;
    }
    iframe,
    video {
      max-width: 100%;
    }
    .wp-block-embed__wrapper,
    .wp-block-video {
      position: relative;
      max-width: 100%;
    }
    .has-text-align-center,
    .aligncenter {
      text-align: center;
      margin-left: auto;
      margin-right: auto;
    }
    .alignleft {
      float: left;
      margin: 0 20px 10px 0;
    }
    .alignright {
      float: right;
      margin: 0 0 10px 20px;
    }
    .wp-block-columns {
      display: flex;
      flex-wrap: wrap;
      gap: 20px;
    }
    .wp-block-column {
      flex: 1 1 280px;
    }
    .wp-block-button__link {
      display: inline-block;
      padding: 11px 20px;
      border-radius: 3px;
      background: #e1000b;
      color: #fff;
      font-weight: 700;
      text-decoration: none;
    }
  }
}
@media (max-width: 767px) {
  .article-content ::v-deep .alignleft,
  .article-content ::v-deep .alignright {
    float: none;
    margin: 0 auto 10px;
  }
}
</style>
